package gr.tech.GRAttestation.sections.tables

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaEllipsisVertical
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.widgets.StoreUserStatusBadge
import gr.tech.GRAttestation.models.stores.STORE_STATUS
import gr.tech.GRAttestation.models.storeusers.STORE_USERS_STATUS
import gr.tech.GRAttestation.models.storeusers.StoreUsersDTO
import gr.tech.GRAttestation.sections.admin.AdminTableContainer
import gr.tech.GRAttestation.styles.admin.AdminTableStickyColumn
import gr.tech.GRAttestation.styles.grstyles.DropdownLinkStyle
import gr.tech.GRAttestation.util.TimeUtils.formatDateOnly
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminStoreUsersTable(
    stores: List<StoreUsersDTO>,
    onView: (StoreUsersDTO) -> Unit,
    onEdit: (StoreUsersDTO) -> Unit,
    onEditStatus: (StoreUsersDTO) -> Unit,
    onDelete: (StoreUsersDTO) -> Unit,
    onRestore: (StoreUsersDTO) -> Unit,
) {
    AdminTableContainer() {
        Table(
            attrs = Modifier
                .classNames("table")
                .minWidth(1000.px)
                .height(100.percent)
                .fillMaxWidth()
                .toAttrs()
        ) {
            Thead(
                attrs = Modifier
                    .styleModifier {
                        property("table-layout", "fixed")
                    }
                    .toAttrs()
            ) {
                Tr {
                    listOf(
                        "Name",
                        "Store",
                        "Email",
                        "Status",
                        "Notes",
                        "Created",
                        "Updated",
                        "Last Login",
                        "Disabled",
                        "Deleted",
                    ).forEach { header ->
                        Th(
                            attrs = Modifier.minWidth(100.px).classNames("col").toAttrs()
                        ) { Text(header) }
                    }
                    Th(
                        attrs = Modifier
                            .then(AdminTableStickyColumn.toModifier())
                            .width(150.px)
                            .toAttrs()
                    ) {
                        Text("Action")
                    }
                }
            }
            Tbody {
                if (stores.isNotEmpty()) {
                    stores.forEach { project ->
                        AdminStoreUserTableRow(
                            user = project,
                            onView = { onView(it) },
                            onEdit = { onEdit(it) },
                            onEditStatus = { onEditStatus(it) },
                            onDelete = { onDelete(it) },
                            onRestore = { onRestore(it) }
                        )
                    }
                }
            }
        }
    }
}


@Composable
fun AdminStoreUserTableRow(
    user: StoreUsersDTO,
    onView: (StoreUsersDTO) -> Unit,
    onEdit: (StoreUsersDTO) -> Unit,
    onEditStatus: (StoreUsersDTO) -> Unit,
    onDelete: (StoreUsersDTO) -> Unit,
    onRestore: (StoreUsersDTO) -> Unit,
) {
    Tr {
        Td {
            Text( value =
                if (user.firstName == null || user.lastName == null) "-"
                else "${user.firstName} ${user.lastName}"
            )
        }
        Td { Text(user.storeName ?: "-") }
        Td { Text(user.email) }
        Td { StoreUserStatusBadge(user.status) }
        Td {
            Div {
                Text(user.notes ?: "-")
            }
        }
        Td {
            Span { Text(formatDateOnly(user.createdAt)) }
            TableDateTimeTooltip(user.createdAt)
        }
        Td {
            Span { Text(formatDateOnly(user.updatedAt)) }
            TableDateTimeTooltip(user.updatedAt)
        }
        Td {
            user.lastLogin?.let {
                Span { Text(formatDateOnly(user.lastLogin)) }
                TableDateTimeTooltip(user.lastLogin)
            } ?: run {
                Text("-")
            }
        }
        Td {
            user.disabledAt?.let {
                Span { Text(formatDateOnly(user.disabledAt)) }
                TableDateTimeTooltip(user.disabledAt)
            } ?: run {
                Text("-")
            }
        }
        Td {
            user.deletedAt?.let {
                Span { Text(formatDateOnly(user.deletedAt)) }
                TableDateTimeTooltip(user.deletedAt)
            } ?: run {
                Text("-")
            }
        }

        // Action column
        Td(
            attrs = Modifier
                .padding(.5.cssRem)
                .then(AdminTableStickyColumn.toModifier())
                .toAttrs()
        ) {
            Div(
                attrs = Modifier
                    .classNames("btn-group")
                    .toAttrs()
            ) {
                Div(
                    attrs = Modifier
                        .classNames("btn-group", "dropstart")
                        .toAttrs()
                ) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-outline-secondary")
                            .toAttrs{
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        FaEllipsisVertical()
                    }
                    Ul(
                        attrs = Modifier.classNames("dropdown-menu")
                            .toAttrs()
                    ) {
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick { onEdit(user) }
                                    .toAttrs()
                            ) {
                                Text("Edit Info")
                            }
                        }
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick { onEditStatus(user) }
                                    .toAttrs()
                            ) {
                                Text("Edit Status")
                            }
                        }
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick {
                                        if(
                                            user.status == STORE_USERS_STATUS.DELETED.bit ||
                                            user.status == STORE_USERS_STATUS.PENDING.bit
                                        ) {
                                            onRestore(user)
                                        } else {
                                            onDelete(user)
                                        }
                                    }
                                    .toAttrs()
                            ) {
                                if(user.status == STORE_STATUS.DELETED.bit) {
                                    Text("Restore")
                                } else {
                                    Text("Delete")
                                }
                            }
                        }
                    }
                }
                Button(
                    attrs = Modifier.classNames("btn", "btn-outline-secondary")
                        .onClick { onEdit(user) }
                        .toAttrs(),
                ) {
                    Text("Edit")
                }
            }
        }
    }
}